import {useEffect, useMemo, useRef, useState} from 'react';
import dayjs from 'dayjs';
import { computeCancellationLabel, CancellationLabelInfo } from '../utils/cancellationLabel';

/**
 * useCancellationLabel computes the current cancellation label and schedules a re-computation
 * exactly at the next threshold boundary to keep UI up-to-date without frequent polling.
 */
export function useCancellationLabel(params: {
  startDate?: string;
  postedStartTime?: string;
  postedStartIso?: string;
}): CancellationLabelInfo | null {
  const [nowIso, setNowIso] = useState<string>(() => dayjs().toISOString());
  const timerRef = useRef<number | null>(null);

  const info = useMemo(() => computeCancellationLabel({...params, nowIso}), [params?.startDate, params?.postedStartTime, params?.postedStartIso, nowIso]);

  useEffect(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (info && info.msUntilNextChange > 0) {
      const delay = Math.min(info.msUntilNextChange, 24 * 3600 * 1000); // cap at 24h safety
      timerRef.current = window.setTimeout(() => setNowIso(dayjs().toISOString()), delay);
    }
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [info?.msUntilNextChange]);

  return info;
}

export default useCancellationLabel;


